/**
 * Analytics Portal V1 — shared funnel (Phase 9, plan §12.2 + C2).
 *
 * Consumes `funnel = {available, partial, reason, notice, stages[]}`. A funnel whose inputs
 * are unavailable is **replaced by the notice** — a partial funnel is never drawn, and a stage
 * is never inferred.
 */
import React from "react";
import { ChartCard } from "./ChartCard";
import { AvailabilityNotice } from "./AvailabilityNotice";

export const FunnelChart = ({ funnel, title = "Conversion funnel", description,
                              testId = "analytics-funnel-primary" }) => {
  if (!funnel) return null;
  if (!funnel.available) {
    return (
      <ChartCard title={title} description={description} testId={testId}>
        <AvailabilityNotice notice={funnel.notice} reason={funnel.reason}
                            testId={`${testId}-unavailable`} />
      </ChartCard>
    );
  }
  const stages = funnel.stages || [];
  const top = stages[0]?.value || 0;
  return (
    <ChartCard
      title={title} description={description} testId={testId}
      summary={stages.map((stage) => `${stage.label}: ${stage.value}`).join("; ")}
    >
      <ol className="space-y-3">
        {stages.map((stage) => {
          const width = top ? Math.max(4, Math.round((stage.value / top) * 100)) : 0;
          return (
            <li key={stage.key} data-testid={`${testId}-stage-${stage.key}`}>
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-[560] text-foreground">{stage.label}</span>
                <span className="tabular-nums text-foreground">
                  {stage.value?.toLocaleString?.() ?? stage.value}
                  {stage.rate_from_previous !== null && stage.rate_from_previous !== undefined && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {stage.rate_from_previous}% of previous step
                    </span>
                  )}
                </span>
              </div>
              <div className="mt-1 h-2.5 w-full overflow-hidden rounded-full bg-accent">
                <div className="h-full rounded-full bg-primary transition-[width] duration-500"
                     style={{ width: `${width}%` }} />
              </div>
            </li>
          );
        })}
      </ol>
    </ChartCard>
  );
};
